import React, { FC, useState, useEffect, useContext } from "react";
import { Store } from "../store";
import { useNavigate } from "react-router-dom";
import types, { ReadUser } from "../store/types";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import {
  usersRequestType,
  usersResponseType,
  userRequestType,
  userResponseType,
} from "../common/type";
import {
  useAuth0Token,
  getUsers,
  getUser,
} from "../common/http-requests";
import { InputFormMolecule } from "../components/molecules/InputFormMolecule";
import { FeedbackBar } from "../components/molecules/FeedbackBar";
import { basicColor, themeColors } from "../common/colors";
import { useTranslation } from 'react-i18next';
import { VisualizedMenus } from "../components/VisualizedMenus";
import { pageMt } from "../common/util";
import { useCustomMediaQuery } from "../common/useCustomMediaQuery";
import PeopleIcon from "@mui/icons-material/People";

const DefaultPageIndex = 0;
const DefaultCountPerPage = 10;
const DefaultCountPerPages = [10, 25, 50];

const LINE_CHART_SIZE: number[] = [
  800, 320
];

const Title = styled(Box)(() => ({
  position: "relative",
  width: '100%',
  height: 'auto',
  maxWidth: `${LINE_CHART_SIZE[0]}px`,
  margin: 'auto',
  borderBottom: `solid 1px ${themeColors[0]}`,
  color: `${themeColors[0]}`,
  textAlign: 'left',
  marginBottom: '2rem',
}));

const UsersPage: FC = () => {
  const { t } = useTranslation();
  const { getToken } = useAuth0Token();
  const navigate = useNavigate();
  const [isSending, setIsSending] = useState(true);
  const [usersData, setUsersData] = useState<usersResponseType>({
    total_count: 0,
    content: [],
  });
  const { isDesktop } = useCustomMediaQuery();

  const { dispatch } = useContext(Store);
  //名前検索に使用する
  const [userNames, setUserNames] = useState<string>("");
  //学籍番号検索に使用する
  const [studentNumbers, setStudentNumbers] = useState<string>("");

  const [pageIndex, setPageIndex] = useState(DefaultPageIndex);
  const [countPerPage, setCountPerPage] = useState(DefaultCountPerPage);

  const columns_detail = [
    { field: "user_name", headerName: t("users.username"), minWidth: 150 },
    { field: "student_number", headerName: t("users.number"), minWidth: 120 },
  ];

  // api取得失敗時のアラート
  const [feedbackInfo, setFeedbackInfo] = useState({
    open: false,
    text: "",
    type: "success" as "error" | "success" | "warning" | "info",
    autoHideDuration: null,
  });
  const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedbackInfo({ ...feedbackInfo, open: false });
  };

  useEffect(() => {
    getUsersData(pageIndex, countPerPage);
  }, []);

  const getUsersData = async (
    pageIndex: number,
    countPerPage: number,
    userName?: string,
    studentNumber?: string
  ) => {
    setIsSending(true);
    try {
      const requestParams: usersRequestType = {
        page_index: pageIndex,
        count_per_page: countPerPage,
        user_names: userName,
        student_numbers: studentNumber,
      };
      const data: usersResponseType = await getUsers(
        await getToken(),
        requestParams
      );
      setUsersData(data);
    } catch {
      setFeedbackInfo({
        ...feedbackInfo,
        open: true,
        text: "学生の利用情報の取得が出来ませんでした。ブラウザをリロードして再度お試しください。",
        type: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  const getReadUserData = async (id: number) => {
    setIsSending(true);
    try {
      const requestParams: userRequestType = {
        email: '',
        login_user_id: id,
      }
      const data: userResponseType = await getUser(
        await getToken(),
        requestParams
      );
      const readUserData: ReadUser = {
        readUserId: id,
        readUserEmail: data.email,
        readUserName: data.user_name,
        readUserNickname: data.nickname,
        readUserType: data.user_type,
        readUserStudentNumber: data.student_number
      }
      return readUserData;
    } catch {
      return null;
    } finally {
      setIsSending(false);
    }
  };

  const handleSetUserName = (e: any) => {
    setUserNames(e.target.value);
  };

  const handleSetStudentNumber = (e: any) => {
    setStudentNumbers(e.target.value);
  };

  const handleClickSearchButton = () => {
    setPageIndex(0);
    getUsersData(0, countPerPage, userNames, studentNumbers);
  };

  const handleClickClearButton = () => {
    setUserNames("");
    setStudentNumbers("");
  };

  const handleChangePage = (event: any, newPage: any) => {
    setPageIndex(newPage);
    getUsersData(newPage, countPerPage, userNames, studentNumbers);
  };

  const handleChangeRowsPerPage = (event: any) => {
    const newCountPerPage = parseInt(event.target.value, DefaultCountPerPage);
    setCountPerPage(newCountPerPage);

    setPageIndex(DefaultPageIndex);
    getUsersData(DefaultPageIndex, newCountPerPage, userNames, studentNumbers);
  };

  const handleLinkClick = async (id: number) => {
    const readUser = await getReadUserData(id);
    dispatch({
      type: types.SET_READ_USER,
      payload: {
        readUser: readUser,
      },
    });
    navigate("/diplomaSupplement");
  };

  return (
    <Container maxWidth="xl">
      <VisualizedMenus />
      <Box mt={pageMt(isDesktop)} className="mb-2 p-2 w-[80%] mx-auto" maxWidth={`${LINE_CHART_SIZE[0]}px`}>
        <Title>
          <Typography
            variant="h5"
            style={{ marginTop: 10, textAlign: "left" }}
          >
            <PeopleIcon sx={{ margin: '.5rem .8rem' }} />{t("sideMenu.users")}
          </Typography>
        </Title>

      {isSending ? (
        <Grid container justifyContent="center" alignItems="center">
          <Grid item>
            <div className="text-center">
              <CircularProgress
                style={{ margin: "10rem auto 0 auto", textAlign: "center" }}
                size="3rem"
              />
            </div>
            <Typography>{t("msg.loading")}</Typography>
          </Grid>
        </Grid>
      ) : (
        <>
          {
            <FeedbackBar
              feedbackInfo={feedbackInfo}
              handleClose={handleClose}
            ></FeedbackBar>
          }
          <div className="flex mb-2 p-2 w-[100%] mx-auto">
            <div className="w-[70%]">
              <InputFormMolecule inputLabel={t("users.username")}>
                <TextField
                  id="outlined-basic"
                  label={t("users.wordComment")}
                  variant="outlined"
                  value={userNames}
                  onChange={(e) => handleSetUserName(e)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleClickSearchButton();
                    }
                  }}
                  style={{ width: "100%" }}
                />
              </InputFormMolecule>
              <InputFormMolecule inputLabel={t("users.number")}>
                <TextField
                  id="outlined-basic"
                  label={t("users.wordComment")}
                  variant="outlined"
                  value={studentNumbers}
                  onChange={(e) => handleSetStudentNumber(e)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleClickSearchButton();
                    }
                  }}
                  style={{ width: "100%" }}
                />
              </InputFormMolecule>
            </div>
            <div className="w-[30%] flex justify-around h-fit">
              <Button
                style={{
                  width: "100px",
                  marginLeft: "20px",
                  fontSize: "16px",
                  background: basicColor,
                }}
                variant="contained"
                onClick={() => handleClickSearchButton()}
              >
                {t("button.search")}
              </Button>
              <Button
                style={{
                  width: "100px",
                  marginLeft: "20px",
                  backgroundColor: "gray",
                  transitionProperty: "opacity",
                  transitionDuration: "300ms",
                  fontSize: "16px",
                }}
                className="hover:opacity-100 opacity-70"
                variant="contained"
                onClick={() => handleClickClearButton()}
              >
                {t("button.clear")}
              </Button>
            </div>
          </div>
          <Paper style={{ width: "100%" }}>
            <TableContainer style={{ maxHeight: 500 }}>
              <Table aria-label="custom pagination table" stickyHeader>
                <TableHead>
                  <TableRow>
                    {columns_detail.map((column) => (
                      <TableCell
                        key={column.field}
                        align="center"
                        style={{ minWidth: column.minWidth }}
                      >
                        {column.headerName}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usersData.content.map((value, index) => (
                    <TableRow
                      key={index}
                      style={
                        index % 2 === 0
                          ? { backgroundColor: "whitesmoke" }
                          : { backgroundColor: "white" }
                      }
                    >
                      <TableCell
                        className="underline cursor-pointer"
                        align="center"
                        onClick={() => handleLinkClick(value.id)}
                      >
                        {value.user_name}
                      </TableCell>
                      <TableCell align="center">
                        {value.student_number}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Table>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    count={usersData.total_count}
                    rowsPerPage={countPerPage}
                    labelRowsPerPage={t("users.pageCount")}
                    rowsPerPageOptions={DefaultCountPerPages}
                    page={pageIndex}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </Paper>
        </>
      )}
      </Box>
    </Container>
  );
};

export default UsersPage;
